import type {
  PipedreamAppConnectionResponse,
} from '../client'
import type {
  IntegrationInstance,
} from '../services/api'

// A user can hold several accounts of one Pipedream app (GET-17441), each a
// connection row with its own label. The first account's label is the app's
// slug, the same convention native integrations use ("notion"), so it renders
// as "Default account" rather than as the raw slug.

export function isDefaultPipedreamAccount(
  connection: PipedreamAppConnectionResponse,
): boolean {
  return connection.label === connection.app_slug
}

export function pipedreamAccountName(
  connection: PipedreamAppConnectionResponse,
): string {
  return isDefaultPipedreamAccount(connection) ? 'Default account' : connection.label
}

// The app's name, plus the account's when it is not the first: what a
// disconnect control is labelled with, so two accounts of one app never share
// an accessible name.
export function pipedreamConnectionTitle(
  connection: PipedreamAppConnectionResponse,
): string {
  return isDefaultPipedreamAccount(connection)
    ? connection.name
    : `${connection.name} (${connection.label})`
}

export function pipedreamAccountsOf(
  connections: PipedreamAppConnectionResponse[],
  appSlug: string,
): PipedreamAppConnectionResponse[] {
  return connections.filter((connection) => connection.app_slug === appSlug)
}

// Whether "Add account" is on offer: the app has accounts and takes more.
export function canAddPipedreamAccount(
  accounts: PipedreamAppConnectionResponse[],
): boolean {
  return accounts.length > 0 && accounts.every((a) => a.allows_multiple_accounts)
}

export function asIntegrationInstances(
  accounts: PipedreamAppConnectionResponse[],
): IntegrationInstance[] {
  return accounts.map((account) => ({
    id: account.id,
    label: account.label,
    connected: true,
    external_user_id: null,
    connected_at: account.connected_at,
  }))
}

// Whether a settled connect landed. Adding an account is only proven by a row
// under its new label: the app's other accounts were there before the attempt.
export function pipedreamConnectLanded(
  connections: PipedreamAppConnectionResponse[],
  appSlug: string,
  accountLabel?: string | undefined,
): boolean {
  return connections.some((connection) => (
    connection.app_slug === appSlug
    && (accountLabel === undefined || connection.label === accountLabel)
  ))
}

export interface PipedreamAppAccounts {
  first: PipedreamAppConnectionResponse
  accounts: PipedreamAppConnectionResponse[]
}

// Connections grouped by app, in the order the apps first appear.
export function groupPipedreamAccounts(
  connections: PipedreamAppConnectionResponse[],
): PipedreamAppAccounts[] {
  const groups = new Map<string, PipedreamAppAccounts>()
  for (const connection of connections) {
    const group = groups.get(connection.app_slug)
    if (group) {
      group.accounts.push(connection)
    } else {
      groups.set(connection.app_slug, { first: connection, accounts: [connection] })
    }
  }
  return [...groups.values()]
}
